import { useEffect, useMemo, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { ItemCombobox } from "@/components/ItemCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IndianRupee, Plus, Pencil, Trash2, Printer, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const RATE_TYPES = ["Retail", "Wholesale", "Custom"];
const BASES = ["MRP", "Purchase Rate"];
const emptyHdr = { rate_name: "", rate_type: "Retail", description: "", effective_date: "", status: "Active" };
const emptyLine = () => ({ item_id: "", calc_base: "MRP", discount_pct: "", custom_rate: "" });

const round2 = (n) => Math.round(n * 100) / 100;
// Items store cost as per_unit_cost; purchase_rate is a legacy/alias field that may be 0
const purchaseRate = (it) => (it?.purchase_rate || it?.per_unit_cost || 0);
const baseVal = (it, base) => (base === "MRP" ? (it?.mrp ?? 0) : purchaseRate(it));
// Calculation Base MRP → Discount % (Rate = MRP − disc%), Purchase Rate → Margin % (Rate = PR + margin%)
const rateFromPct = (it, base, pct) => {
  if (pct === "" || pct === null || isNaN(Number(pct))) return "";
  return round2(baseVal(it, base) * (base === "MRP" ? (1 - Number(pct) / 100) : (1 + Number(pct) / 100)));
};
const pctFromRate = (it, base, rate) => {
  if (rate === "" || rate === null || isNaN(Number(rate))) return "";
  const b = baseVal(it, base);
  if (!b) return "";
  return round2(base === "MRP" ? (1 - Number(rate) / b) * 100 : (Number(rate) / b - 1) * 100);
};

const StatusBadge = ({ s }) => (
  <span className={`px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider border ${
    s === "Active" ? "text-emerald-500 bg-emerald-500/10 border-emerald-500/30" : "text-muted-foreground bg-secondary border-border"
  }`}>{s}</span>
);

export default function RateMaster() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [hdr, setHdr] = useState(emptyHdr);
  const [lines, setLines] = useState([emptyLine()]);
  const [bulk, setBulk] = useState({ base: "MRP", pct: "" });
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({ company: "all", category: "all", group: "all" });

  const load = () => api.get("/rate-masters").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/items?scope=all").then(r => setItems(r.data)).catch(() => {});
    api.get("/companies").then(r => setCompanies(r.data)).catch(() => {});
    api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
  }, []);

  // Full-screen form: hide sidebar/top-nav while the rate form is open
  useEffect(() => {
    document.body.classList.toggle("txn-form-fullscreen", open);
    return () => document.body.classList.remove("txn-form-fullscreen");
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => document.querySelector('[data-testid="rm-rate-name"]')?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  const openNew = () => { setHdr(emptyHdr); setLines([emptyLine()]); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setHdr({
      rate_name: r.rate_name, rate_type: r.rate_type || "Retail",
      description: r.description || "", effective_date: r.effective_date || "", status: r.status || "Active",
    });
    const ls = (r.lines || []).map(l => ({ item_id: l.item_id, calc_base: l.calc_base || "MRP", discount_pct: l.discount_pct ?? "", custom_rate: l.custom_rate }));
    setLines(ls.length ? ls : [emptyLine()]);
    setEditId(r.id); setOpen(true);
  };
  const closeForm = () => { setOpen(false); setEditId(null); setHdr(emptyHdr); setLines([emptyLine()]); };

  const setLine = (i, k, v) => setLines(ls => ls.map((l, x) => x === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, emptyLine()]);
  const removeLine = (i) => setLines(ls => ls.filter((_, x) => x !== i));

  const matchItems = (f) => items.filter(it =>
    (f.company === "all" || it.company === f.company) &&
    (f.category === "all" || it.category === f.category) &&
    (f.group === "all" || it.group === f.group));

  const hasUnsavedRates = () => lines.some(l => l.item_id && (l.discount_pct !== "" || l.custom_rate !== ""));
  const confirmRefresh = () => !hasUnsavedRates() || confirm("Unsaved rate entries in the grid will be lost on refresh. Continue?");

  const populate = (f) => {
    const match = matchItems(f);
    setLines(match.map(it => ({ item_id: it.id, calc_base: "MRP", discount_pct: "", custom_rate: "" })));
    toast.success(`Loaded ${match.length} item${match.length === 1 ? "" : "s"}`);
  };

  const loadItems = (f = filters) => { if (confirmRefresh()) populate(f); };

  const onFilterChange = (k, v) => {
    if (!confirmRefresh()) return; // cancel → filter selection reverts, grid untouched
    const nf = { ...filters, [k]: v };
    setFilters(nf);
    populate(nf);
  };

  const matchCount = useMemo(() => matchItems(filters).length, [items, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Editing Calculation Base re-derives Rate from the existing Discount %
  const onCalcBase = (i, v) => setLines(ls => ls.map((l, x) => {
    if (x !== i) return l;
    const it = items.find(y => y.id === l.item_id);
    const nl = { ...l, calc_base: v };
    if (l.discount_pct !== "") nl.custom_rate = rateFromPct(it, v, l.discount_pct);
    return nl;
  }));
  // Entering Discount % → Rate auto-calculates per the row's Calculation Base
  const onPct = (i, v) => setLines(ls => ls.map((l, x) => {
    if (x !== i) return l;
    const it = items.find(y => y.id === l.item_id);
    return { ...l, discount_pct: v, custom_rate: v === "" ? "" : rateFromPct(it, l.calc_base, v) };
  }));
  // Entering Rate directly → Discount % auto-calculates per the row's Calculation Base
  const onRate = (i, v) => setLines(ls => ls.map((l, x) => {
    if (x !== i) return l;
    const it = items.find(y => y.id === l.item_id);
    return { ...l, custom_rate: v, discount_pct: v === "" ? "" : pctFromRate(it, l.calc_base, v) };
  }));

  const applyBulk = () => {
    const pct = Number(bulk.pct) || 0;
    setLines(ls => ls.map(l => {
      if (!l.item_id) return l;
      const it = items.find(x => x.id === l.item_id);
      if (!it) return l;
      return { ...l, calc_base: bulk.base, discount_pct: bulk.pct, custom_rate: rateFromPct(it, bulk.base, bulk.pct) };
    }));
    toast.success(`Applied ${pct}% ${bulk.base === "MRP" ? "discount on MRP" : "margin on purchase rate"} to all items`);
  };

  const save = async () => {
    try {
      const payload = {
        ...hdr,
        description: hdr.description || null,
        effective_date: hdr.effective_date || null,
        lines: lines.filter(l => l.item_id).map(l => {
          const it = items.find(x => x.id === l.item_id);
          return {
            item_id: l.item_id,
            item_name: it?.item_name || "",
            part_number: it?.part_number || "",
            unit: it?.unit || "pcs",
            standard_rate: it?.mrp ?? 0,
            purchase_rate: purchaseRate(it),
            calc_base: l.calc_base || "MRP",
            discount_pct: Number(l.discount_pct) || 0,
            min_margin_pct: 0,
            custom_rate: Number(l.custom_rate) || 0,
          };
        }),
      };
      if (editId) await api.patch(`/rate-masters/${editId}`, payload);
      else await api.post("/rate-masters", payload);
      toast.success(editId ? "Rate updated" : "Rate created");
      closeForm(); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (r) => {
    if (!confirm(`Delete rate "${r.rate_name}"? This cannot be undone.`)) return;
    try { await api.delete(`/rate-masters/${r.id}`); toast.success("Rate deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const printRate = async (r) => {
    try {
      const res = await api.get(`/rate-masters/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---------- Full-screen create / edit page ----------
  if (open) {
    return (
      <div className="p-8" onKeyDownCapture={handleSimpleFormNav}>
        <div className="border border-border bg-card rounded-sm p-6 min-h-[70vh]" data-testid="rate-form-page">
          <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
            <div className="flex items-center gap-3">
              <Button type="button" data-testid="rm-back-btn" variant="outline" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider"><ArrowLeft className="w-3 h-3 mr-1"/>Back to List</Button>
              <h2 className="font-display uppercase text-lg">{editId ? "Edit" : "New"} Rate</h2>
            </div>
            <Button type="button" data-testid="rm-cancel-btn" variant="ghost" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground"><X className="w-3 h-3 mr-1"/>Cancel</Button>
          </div>

          <div className="grid grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rate Name *</Label>
              <Input data-testid="rm-rate-name" value={hdr.rate_name} onChange={(e) => setHdr(x => ({ ...x, rate_name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="e.g. Diwali Retail 2026"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rate Type</Label>
              <Select value={hdr.rate_type} onValueChange={(v) => setHdr(x => ({ ...x, rate_type: v }))}>
                <SelectTrigger data-testid="rm-rate-type" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{RATE_TYPES.map(t => <SelectItem key={t} data-testid={`rm-rate-type-opt-${t}`} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Effective Date</Label>
              <Input data-testid="rm-effective-date" type="date" value={hdr.effective_date} onChange={(e) => setHdr(x => ({ ...x, effective_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</Label>
              <Select value={hdr.status} onValueChange={(v) => setHdr(x => ({ ...x, status: v }))}>
                <SelectTrigger data-testid="rm-status" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="rm-status-opt-Active" value="Active">Active</SelectItem>
                  <SelectItem data-testid="rm-status-opt-Inactive" value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
            <Input data-testid="rm-description" value={hdr.description} onChange={(e) => setHdr(x => ({ ...x, description: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" placeholder="Optional note about this rate card"/>
          </div>

          <div className="flex flex-wrap items-end gap-3 mb-2 p-3 border border-border bg-card rounded-sm" data-testid="item-filter-bar">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Company</Label>
              <Select value={filters.company} onValueChange={(v) => onFilterChange("company", v)}>
                <SelectTrigger data-testid="rm-filter-company" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem data-testid="rm-filter-company-opt-all" value="all">All companies</SelectItem>{companies.map(c => <SelectItem key={c.id} data-testid={`rm-filter-company-opt-${c.id}`} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
              <Select value={filters.category} onValueChange={(v) => onFilterChange("category", v)}>
                <SelectTrigger data-testid="rm-filter-category" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem data-testid="rm-filter-category-opt-all" value="all">All categories</SelectItem>{categories.map(c => <SelectItem key={c.id} data-testid={`rm-filter-category-opt-${c.id}`} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Group</Label>
              <Select value={filters.group} onValueChange={(v) => onFilterChange("group", v)}>
                <SelectTrigger data-testid="rm-filter-group" className="mt-1 rounded-sm bg-secondary border-border w-44"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem data-testid="rm-filter-group-opt-all" value="all">All groups</SelectItem>{groups.map(g => <SelectItem key={g.id} data-testid={`rm-filter-group-opt-${g.id}`} value={g.name}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button type="button" data-testid="rm-load-items" variant="outline" onClick={() => loadItems()} className="rounded-sm text-xs uppercase tracking-wider">Load Items</Button>
            <div className="text-xs text-muted-foreground pb-2 ml-auto" data-testid="rm-match-count">{matchCount} item{matchCount === 1 ? "" : "s"} match</div>
          </div>
          <div className="flex items-end gap-3 mb-2 p-3 border border-border bg-secondary/40 rounded-sm" data-testid="bulk-calc-bar">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Calculation Base</Label>
              <Select value={bulk.base} onValueChange={(v) => setBulk(x => ({ ...x, base: v }))}>
                <SelectTrigger data-testid="bulk-base" className="mt-1 rounded-sm bg-secondary border-border w-40"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem data-testid="bulk-base-opt-MRP" value="MRP">MRP</SelectItem>
                  <SelectItem data-testid="bulk-base-opt-Purchase Rate" value="Purchase Rate">Purchase Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{bulk.base === "MRP" ? "Discount %" : "Margin %"}</Label>
              <Input data-testid="bulk-pct" type="number" min="0" value={bulk.pct} onChange={(e) => setBulk(x => ({ ...x, pct: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border w-28" placeholder="0"/>
            </div>
            <Button type="button" data-testid="bulk-apply" onClick={applyBulk} disabled={bulk.pct === "" || !lines.some(l => l.item_id)} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">Apply</Button>
          </div>

          <div className="border border-border rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-center p-2">Item Name</th>
                  <th className="text-center p-2 w-28">Part No</th>
                  <th className="text-center p-2 w-24">HSN Code</th>
                  <th className="text-center p-2 w-16">Unit</th>
                  <th className="text-center p-2 w-28">MRP</th>
                  <th className="text-center p-2 w-36">Purchase Rate</th>
                  <th className="text-center p-2 w-32">Base</th>
                  <th className="text-center p-2 w-24">Percent %</th>
                  <th className="text-center p-2 w-28">Rate</th>
                  <th className="p-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const it = items.find(x => x.id === l.item_id);
                  const warn = it && Number(l.custom_rate) > 0 && Number(l.custom_rate) < purchaseRate(it);
                  return (
                    <tr key={i} data-testid={`rm-line-${i}`} className={`border-t border-border ${warn ? "bg-destructive/10" : ""}`}>
                      <td className="p-1">
                        <ItemCombobox items={items} value={l.item_id} onSelect={(x) => setLine(i, "item_id", x.id)} testId={`rm-item-${i}`}
                          labelFn={(x) => `${x.item_code ? x.item_code + " · " : ""}${x.item_name} · MRP ₹${x.mrp}`}/>
                      </td>
                      <td className="p-1"><Input data-testid={`rm-partno-${i}`} value={it?.part_number || ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`rm-hsn-${i}`} value={it?.hsn_code || ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`rm-unit-${i}`} value={it?.unit || ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`rm-stdrate-${i}`} value={it ? inr(it.mrp ?? 0) : ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs text-right font-mono text-muted-foreground"/></td>
                      <td className="p-1"><Input data-testid={`rm-purchrate-${i}`} value={it ? inr(purchaseRate(it)) : ""} readOnly className="h-9 rounded-sm bg-secondary border-border text-xs text-right font-mono text-muted-foreground"/></td>
                      <td className="p-1">
                        <Select value={l.calc_base} onValueChange={(v) => onCalcBase(i, v)}>
                          <SelectTrigger data-testid={`rm-calcbase-${i}`} className="h-9 rounded-sm bg-secondary border-border text-xs w-32"><SelectValue/></SelectTrigger>
                          <SelectContent>{BASES.map(b => <SelectItem key={b} data-testid={`rm-calcbase-${i}-opt-${b}`} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-1"><Input data-testid={`rm-discpct-${i}`} type="number" value={l.discount_pct} onChange={(e) => onPct(i, e.target.value)} className="h-9 rounded-sm bg-secondary border-border text-xs text-right"/></td>
                      <td className="p-1"><Input data-testid={`rm-customrate-${i}`} type="number" min="0" value={l.custom_rate} onChange={(e) => onRate(i, e.target.value)} className={`h-9 rounded-sm bg-secondary border-border text-xs text-right ${warn ? "border-destructive text-destructive" : ""}`}/></td>
                      <td className="p-1 text-center">
                        {lines.length > 1 && <Button type="button" data-testid={`rm-del-line-${i}`} size="sm" variant="ghost" onClick={() => removeLine(i)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Button type="button" data-testid="rm-add-line" variant="outline" onClick={addLine} className="mt-2 rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/>Add Item</Button>

          <div className="flex justify-end gap-2 mt-6 border-t border-border pt-4">
            <Button type="button" variant="ghost" onClick={closeForm} className="rounded-sm text-xs uppercase tracking-wider">Cancel</Button>
            <Button data-testid="rm-save-btn" onClick={save} disabled={!hdr.rate_name || !lines.some(l => l.item_id)} className="rounded-sm bg-primary text-primary-foreground text-xs uppercase tracking-wider">
              {editId ? "Update Rate" : "Save Rate"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- List page ----------
  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><IndianRupee className="w-3 h-3"/> Inventory · Masters</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Rate Master</h1>
        </div>
        <Button data-testid="add-rate-btn" onClick={openNew} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] font-bold text-xs">
          <Plus className="w-4 h-4 mr-1"/> New Rate
        </Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="rate-masters-table">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Rate Name</th>
              <th className="text-left p-3">Rate Type</th>
              <th className="text-left p-3">Effective Date</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground" data-testid="rates-empty">No rates yet — click New Rate to create one</td></tr>}
            {pg.pageItems.map(r => (
              <tr key={r.id} data-testid={`rm-row-${r.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-semibold">{r.rate_name}</td>
                <td className="p-3 text-xs">{r.rate_type}</td>
                <td className="p-3 text-xs font-mono">{r.effective_date || "-"}</td>
                <td className="p-3" data-testid={`rm-status-${r.id}`}><StatusBadge s={r.status}/></td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  <Button data-testid={`rm-edit-${r.id}`} size="sm" variant="ghost" title="Edit" onClick={() => openEdit(r)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>
                  <Button data-testid={`rm-del-${r.id}`} size="sm" variant="ghost" title="Delete" onClick={() => del(r)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                  <Button data-testid={`rm-print-${r.id}`} size="sm" variant="ghost" title="Print / PDF" onClick={() => printRate(r)} className="h-7 w-7 p-0"><Printer className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
